import React, { useState } from 'react';
import { ArrowRightLeft, ShieldCheck } from 'lucide-react';
import { useContact } from '../../context/ContactContext';

// Row 1 Transfer, Phase 1 (2026-09-28): queue and phone-number targets only.
// Lives directly under the Transfer control in the LEFT rail (Storm, 2026-09-29:
// the options belong with the button that opens them, not in the right rail).
// Agent target is validated server-side (knktr-transfer-prepare) but has no
// UI here yet — pending a live-call test of direct Endpoint construction.
//
// Queue has no dropdown at all: there is only ever one valid queue per
// contact (its own tenant's), resolved server-side by the prepare Lambda —
// showing a picker with a choice that isn't really a choice would just
// invite a wrong click. Phone number reuses OutboundDialer's exact two-step
// confirm shape (confirming state, second click to commit) — the same real-
// external-consequence discipline for the same class of action.
export default function TransferPanel() {
  const {
    transferPanelOpen, setTransferPanelOpen, display, handleTransfer,
    transferPending, transferError, transferPartial,
  } = useContact();
  const [confirming, setConfirming] = useState(null); // null | 'queue' | 'phone_number'
  const [phoneNumber, setPhoneNumber] = useState('');

  if (!transferPanelOpen) return null;

  const brandName = display?.brandName || 'this tenant';
  // A prior attempt on this contact left the connection state ambiguous
  // (see ContactContext.jsx handleTransfer's own guard) — the SoftphoneBanner
  // already carries the persistent, non-dismissible notice; this panel just
  // stops offering a second attempt on top of an unverified first one.
  const disabled = transferPending || transferPartial;

  const confirmQueue = () => {
    if (confirming !== 'queue') { setConfirming('queue'); return; }
    handleTransfer('queue');
    setConfirming(null);
  };

  const submitPhone = event => {
    event.preventDefault();
    if (confirming !== 'phone_number') { setConfirming('phone_number'); return; }
    handleTransfer('phone_number', { destinationNumber: phoneNumber });
    setConfirming(null);
  };

  return (
    <div className="transfer-panel" role="region" aria-label="Transfer options">
      <div className="transfer-panel-title">
        <ArrowRightLeft size={13} />
        Transfer
        <button type="button" className="transfer-panel-close" onClick={() => setTransferPanelOpen(false)}>
          Close
        </button>
      </div>

      <div className="transfer-target">
        <p className="transfer-target-label">Transfer to {brandName}&rsquo;s queue</p>
        {confirming === 'queue' && !transferPending && (
          <p className="transfer-confirm" role="alert">
            <ShieldCheck size={13} />
            Confirm: transfer this call to {brandName}&rsquo;s queue?
          </p>
        )}
        <button type="button" onClick={confirmQueue} disabled={disabled}>
          {transferPending ? 'Transferring…' : confirming === 'queue' ? 'Confirm & transfer' : 'Transfer to queue'}
        </button>
        {confirming === 'queue' && !transferPending && (
          <button type="button" className="transfer-cancel" onClick={() => setConfirming(null)}>
            Cancel
          </button>
        )}
      </div>

      <form className="transfer-target" onSubmit={submitPhone}>
        <label>
          Transfer to a phone number
          <input
            type="tel"
            value={phoneNumber}
            onChange={event => { setPhoneNumber(event.target.value); setConfirming(null); }}
            placeholder="+61…"
            autoComplete="tel"
            required
          />
        </label>
        {confirming === 'phone_number' && !transferPending && (
          <p className="transfer-confirm" role="alert">
            <ShieldCheck size={13} />
            Confirm: transfer this call to <strong>{phoneNumber}</strong>?
          </p>
        )}
        <button type="submit" disabled={disabled || !phoneNumber.trim()}>
          {transferPending ? 'Transferring…' : confirming === 'phone_number' ? 'Confirm & transfer' : 'Transfer to number'}
        </button>
        {confirming === 'phone_number' && !transferPending && (
          <button type="button" className="transfer-cancel" onClick={() => setConfirming(null)}>
            Cancel
          </button>
        )}
      </form>

      {transferError && <p className="transfer-error" role="alert">{transferError}</p>}
    </div>
  );
}
